import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useUserProfile } from "@/hooks/useUserProfile";

export type WorkspaceMode = "business" | "member";

const key = (uid: string) => `bb:workspace:${uid}`;
const EVENT = "bb:workspace-change";

export const getStoredWorkspaceMode = (uid: string): WorkspaceMode | null => {
  try {
    const v = localStorage.getItem(key(uid));
    return v === "business" || v === "member" ? v : null;
  } catch {
    return null;
  }
};

/**
 * Business workspace ("hosting") vs member view ("travelling").
 * Business-only and explicitly dual-role accounts can switch workspaces.
 */
export const useWorkspaceMode = () => {
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const isBusinessOnly = profile?.role === "other";

  const canUseBusiness = !!user && (isBusinessOnly || profile?.is_business_owner === true);

  const [stored, setStored] = useState<WorkspaceMode | null>(() => (user ? getStoredWorkspaceMode(user.id) : null));
  useEffect(() => {
    if (!user) return;
    const sync = () => setStored(getStoredWorkspaceMode(user.id));
    sync();
    window.addEventListener(EVENT, sync);
    return () => window.removeEventListener(EVENT, sync);
  }, [user]);

  const setMode = useCallback(
    (m: WorkspaceMode) => {
      if (!user) return;
      try { localStorage.setItem(key(user.id), m); } catch { /* ignore */ }
      window.dispatchEvent(new Event(EVENT));
    },
    [user]
  );

  // Business-only accounts and dual-role business owners default to business; everyone else to member view.
  const mode: WorkspaceMode = stored ?? (canUseBusiness ? "business" : "member");

  return { canUseBusiness, isBusinessOnly, mode, setMode, profileLoaded: !!profile };
};
